// Copyright ©️ 2026 eVolve MEP, LLC

import { type TimeFrameData, useEvolveGet } from '@evolvemep/api-ts';
import { Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { convertTimeFrameDataToRequestParams } from 'components/Mantine/TimeFramePicker/helpers';
import { useMantineNotifications } from 'components/Mantine/useMantineNotifications';

type Props = {
  timeFrame: TimeFrameData;
};

export const ExportAnalyticsButton = ({ timeFrame }: Props) => {
  const { showError } = useMantineNotifications();
  const { apiCall, loading } = useEvolveGet('usage-stats-retriever/v1/query');

  return (
    <AgGridStyleTooltip label="Export CSV" withArrow>
      <Button
        onClick={() => {
          void apiCall({
            params: convertTimeFrameDataToRequestParams(timeFrame),
          })
            .then(({ downloadUrl }) => {
              const downloadWindow = window.open(downloadUrl, '_blank');
              if (downloadWindow?.closed ?? true) {
                notifications.show({
                  position: 'top-right',
                  message:
                    'If your download does not begin automatically, please check your browser’s pop-up blocker settings.',
                });
              }
            })
            .catch((e) => {
              // a 403 from this endpoint typically means the Company is missing the proper license
              // oxlint-disable-next-line typescript/no-unsafe-member-access
              if (e?.status === 403) {
                notifications.show({
                  title: 'No download available.',
                  message: (
                    <>
                      EVOLVE provides access to download historical usage data. Please contact{' '}
                      <a href="mailto:support@evolvemep.com">support@evolvemep.com</a> for licensing information.
                    </>
                  ),
                  color: 'red',
                });
              } else {
                showError(e);
              }
            });
        }}
        variant="outline"
        loading={loading}
      >
        <EvolveIcon icon="Export" size="sm" />
      </Button>
    </AgGridStyleTooltip>
  );
};
